'use client'

import type { Media as MediaType } from '@/payload-types'

import { Media } from '@/components/Media'
import { useSearchParams } from 'next/navigation'
import React, { useEffect } from 'react'

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { GalleryZoomImage } from './GalleryZoomImage'

type Props = {
  images: MediaType[]
}

export const Gallery: React.FC<Props> = ({ images }) => {
  const searchParams = useSearchParams()
  const [current, setCurrent] = React.useState(0)
  const [api, setApi] = React.useState<CarouselApi>()

  useEffect(() => {
    if (!api) {
      return
    }

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const handlePrevious = () => {
    const newIndex = Math.max(0, current - 1)
    setCurrent(newIndex)
    api?.scrollTo(newIndex)
  }

  const handleNext = () => {
    const newIndex = Math.min(images.length - 1, current + 1)
    setCurrent(newIndex)
    api?.scrollTo(newIndex)
  }

  if (!images.length) return null

  return (
    <div className="flex flex-col gap-4">
      <div className="relative w-full overflow-hidden rounded-xl border border-border/50 bg-gradient-to-b from-background/90 to-background shadow-sm group">
        <div className="aspect-square relative w-full flex items-center justify-center [&_.iiz]:w-full [&_.iiz]:h-full [&_.iiz__img]:object-contain [&_.iiz__img]:w-full [&_.iiz__img]:h-full">
          {images[current] && (
            <GalleryZoomImage image={images[current]} />
          )}
        </div>
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-[var(--neon-orange)] bg-background/80 hover:bg-background text-[var(--neon-orange)] shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed z-10 hover:shadow-[0_0_10px_var(--neon-orange)]"
              disabled={current === 0}
              aria-label="Previous image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-left h-5 w-5"
              >
                <path d="m12 19-7-7 7-7" />
                <path d="M19 12H5" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-[var(--neon-orange)] bg-background/80 hover:bg-background text-[var(--neon-orange)] shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed z-10 hover:shadow-[0_0_10px_var(--neon-orange)]"
              disabled={current === images.length - 1}
              aria-label="Next image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-right h-5 w-5"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="relative">
          <Carousel setApi={setApi} className="w-full" opts={{ align: 'start', loop: false }}>
            <CarouselContent className="-ml-2">
              {images.map((image, i) => {
                const isVideo = image?.mimeType?.includes('video')

                return (
                  <CarouselItem
                    className="basis-1/4 sm:basis-1/5 pl-2 cursor-pointer"
                    key={`${image.id}-${i}`}
                    onClick={() => {
                      setCurrent(i)
                      api?.scrollTo(i)
                    }}
                  >
                    <div
                      className={`relative aspect-square overflow-hidden rounded-lg border transition-all ${
                        i === current
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-border/50 hover:border-primary/50'
                      }`}
                    >
                      <Media
                        resource={image}
                        fill
                        className="h-full w-full"
                        imgClassName="h-full w-full object-cover"
                        videoClassName="h-full w-full object-cover"
                      />
                      {isVideo && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-white shadow-lg">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="h-5 w-5 ml-0.5"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </CarouselItem>
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      )}
    </div>
  )
}
